import Cart from "cart-store";

const HOVER_MEDIA_QUERY = "(hover: hover)";

class FalconHeader extends HTMLElement {
    #abortController = null;
    #unsubscribeCart = null;
    #items = [];
    #current = null;
    #trigger = null;
    #restoringFocus = false;
    #hoverMedia = window.matchMedia(HOVER_MEDIA_QUERY);

    connectedCallback() {
        this.#abortController = new AbortController();
        const { signal } = this.#abortController;

        this.#items = [...this.querySelectorAll(".falcon-nav-item[data-falcon-menu]")];
        this.#items.forEach(item => this.#bindItem(item, signal));

        this.addEventListener("click", event => {
            if (event.target.closest(".falcon-mega-close")) this.close(true);
        }, { signal });
        this.querySelector(".falcon-header")?.addEventListener("pointerleave", () => this.close(), { signal });
        document.addEventListener("pointerdown", event => {
            if (!this.contains(event.target)) this.close();
        }, { signal });
        document.addEventListener("keydown", event => {
            if (event.key === "Escape") this.close(true);
        }, { signal });
        document.addEventListener("shopify:block:select", event => this.#openForEditorBlock(event.target), { signal });
        document.addEventListener("shopify:block:deselect", () => this.close(), { signal });

        this.#subscribeCartCount();
    }

    disconnectedCallback() {
        this.#abortController?.abort();
        this.#unsubscribeCart?.();
        this.#unsubscribeCart = null;
    }

    open(item, trigger) {
        const panel = this.#panelFor(item);
        if (!panel) return;

        this.#current = item;
        this.#trigger = trigger || this.#trigger;
        this.#items.forEach(other => {
            const active = other === item;
            const otherPanel = this.#panelFor(other);
            other.classList.toggle("is-active", active);
            other.querySelector(".falcon-nav-toggle")?.setAttribute("aria-expanded", String(active));
            if (otherPanel) otherPanel.hidden = !active;
        });
    }

    close(restoreFocus = false) {
        if (!this.#current) return;

        this.#items.forEach(item => {
            const panel = this.#panelFor(item);
            item.classList.remove("is-active");
            item.querySelector(".falcon-nav-toggle")?.setAttribute("aria-expanded", "false");
            if (panel) panel.hidden = true;
        });
        this.#current = null;

        if (restoreFocus && this.#trigger) {
            // Refocusing the toggle fires focusin on its item, which would immediately reopen the panel.
            this.#restoringFocus = true;
            this.#trigger.focus();
            this.#restoringFocus = false;
        }
    }

    #bindItem(item, signal) {
        const toggle = item.querySelector(".falcon-nav-toggle");

        toggle?.addEventListener("click", event => {
            event.preventDefault();
            this.open(item, toggle);
        }, { signal });
        item.addEventListener("pointerenter", () => {
            if (this.#hoverMedia.matches) this.open(item, toggle);
        }, { signal });
        item.addEventListener("focusin", () => {
            if (!this.#restoringFocus) this.open(item, toggle);
        }, { signal });
    }

    #panelFor(item) {
        const id = item.dataset.falconMenu;
        return id ? document.getElementById(id) : null;
    }

    #openForEditorBlock(target) {
        if (!(target instanceof Element) || !this.contains(target)) return;
        const panel = target.closest(".falcon-mega");
        const item = panel && this.#items.find(candidate => candidate.dataset.falconMenu === panel.id);
        if (item) this.open(item, item.querySelector(".falcon-nav-toggle"));
    }

    #subscribeCartCount() {
        const bubble = this.querySelector(".falcon-bag-count");
        if (!bubble) return;

        try {
            this.#unsubscribeCart = Cart.subscribe(state => {
                const count = Number(state?.cart?.item_count) || 0;
                bubble.hidden = count === 0;
            });
        } catch (error) {
            console.error("falcon-header: could not subscribe to cart updates", error);
        }
    }
}

if (!customElements.get("falcon-header")) {
    customElements.define("falcon-header", FalconHeader);
}
